// Phase COTTON-RM-002 Part C — Cotton Cost Adjustment Report
//
// All adjustments across GRNs with status / impact / downstream warnings.
// Filters: approval_status, finance_posting_status, downstream_only.

import { useMemo, useState } from 'react'
import {
  Button, Card, Empty, Select, Skeleton, Space, Switch, Table, Tag,
  Typography,
} from 'antd'
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import {
  cottonRmApiPartC, type CostAdjustmentReportRow,
} from '@/features/cotton-rm/cottonRmApi'

const { Text, Title } = Typography

function statusColor(s: string) {
  if (s === 'APPROVED') return 'success'
  if (s === 'PENDING')  return 'warning'
  if (s === 'REJECTED') return 'error'
  if (s === 'POSTED')   return 'blue'
  if (s === 'STAGED')   return 'cyan'
  return 'default'
}

function confidenceColor(c: string) {
  if (c === 'EXACT')       return 'success'
  if (c === 'INFERRED')    return 'blue'
  if (c === 'ESTIMATED')   return 'warning'
  if (c === 'PROVISIONAL') return 'gold'
  return 'default'
}

function csvDownload(rows: CostAdjustmentReportRow[]) {
  if (rows.length === 0) return
  const cols = Object.keys(rows[0])
  const header = cols.join(',')
  const body = rows.map(r =>
    cols.map(c => JSON.stringify(
      (r as unknown as Record<string, unknown>)[c] ?? '',
    )).join(',')).join('\n')
  const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = 'cotton-cost-adjustments.csv'
  a.click()
  URL.revokeObjectURL(a.href)
}


export default function CottonCostAdjustmentReportPage() {
  const [approval, setApproval] = useState<string | undefined>()
  const [finance, setFinance] = useState<string | undefined>()
  const [downstreamOnly, setDownstreamOnly] = useState(false)

  const { data: rows = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ['cost-adj-report', approval, finance, downstreamOnly],
    queryFn: () => cottonRmApiPartC.costAdjReport({
      approval_status: approval,
      finance_status:  finance,
      downstream_only: downstreamOnly || undefined,
      limit: 1000,
    }),
  })

  const totals = useMemo(() => {
    const t = { count: rows.length, approved: 0, pending: 0, signed: 0, warned: 0 }
    rows.forEach(r => {
      if (r.approval_status === 'APPROVED') t.approved += 1
      if (r.approval_status === 'PENDING')  t.pending += 1
      if (r.downstream_warning) t.warned += 1
      t.signed += r.signed_amount || 0
    })
    return t
  }, [rows])

  const cols = [
    { title: 'GRN', dataIndex: 'grn_no',
      render: (v: string, r: CostAdjustmentReportRow) =>
        <Link to={`/grn/${r.grn_id}`}>{v}</Link> },
    { title: 'Supplier', dataIndex: 'supplier_name', ellipsis: true },
    { title: 'Type', dataIndex: 'adjustment_type',
      render: (v: string) => <Tag>{v}</Tag> },
    { title: 'Amount', dataIndex: 'signed_amount', align: 'right' as const,
      render: (n: number) => (
        <Text style={{ color: n < 0 ? '#ff4d4f' : '#52c41a' }}>
          {n < 0 ? '−' : '+'}₹{Math.abs(n).toLocaleString()}
        </Text>
      ) },
    { title: 'Confidence', dataIndex: 'calculation_confidence',
      render: (c: string) => <Tag color={confidenceColor(c)}>{c}</Tag> },
    { title: 'Approval', dataIndex: 'approval_status',
      render: (s: string) => <Tag color={statusColor(s)}>{s}</Tag> },
    { title: 'Finance', dataIndex: 'finance_posting_status',
      render: (s: string) => <Tag color={statusColor(s)}>{s}</Tag> },
    { title: 'Reason', dataIndex: 'reason', ellipsis: true },
    { title: 'Downstream', dataIndex: 'downstream_warning',
      render: (w: string | null) => w
        ? <Tag color="warning">Issued/consumed bales</Tag>
        : '' },
    { title: 'Created', dataIndex: 'created_at',
      render: (v: string) => new Date(v).toLocaleString() },
  ]

  return (
    <div style={{ padding: 16, maxWidth: 1600, margin: '0 auto' }}>
      <Title level={2} style={{ marginTop: 0 }}>Cost Adjustment Report</Title>
      <Text type="secondary">
        Every freight / brokerage / discount / supplier-claim across all
        GRNs. Filter by status, finance-posting state, or downstream
        impact.
      </Text>

      <Space wrap style={{ marginTop: 12, marginBottom: 8 }}>
        <Select placeholder="Approval status" allowClear style={{ width: 180 }}
                value={approval} onChange={setApproval}
                options={['PENDING', 'APPROVED', 'REJECTED', 'POSTED']
                  .map(v => ({ value: v }))} />
        <Select placeholder="Finance status" allowClear style={{ width: 180 }}
                value={finance} onChange={setFinance}
                options={['PENDING', 'STAGED', 'POSTED']
                  .map(v => ({ value: v }))} />
        <Space size={6}>
          <Switch checked={downstreamOnly} onChange={setDownstreamOnly} />
          <Text>Downstream warnings only</Text>
        </Space>
        <Button icon={<ReloadOutlined spin={isFetching} />}
                onClick={() => refetch()}>Refresh</Button>
        <Button icon={<DownloadOutlined />}
                disabled={rows.length === 0}
                onClick={() => csvDownload(rows)}>Export CSV</Button>
      </Space>

      <Space wrap style={{ marginBottom: 12 }}>
        <Tag color="default">Total: {totals.count}</Tag>
        <Tag color="warning">Pending: {totals.pending}</Tag>
        <Tag color="success">Approved: {totals.approved}</Tag>
        <Tag color="red">Downstream: {totals.warned}</Tag>
        <Tag color={totals.signed >= 0 ? 'green' : 'red'}>
          Signed total: {totals.signed < 0 ? '−' : '+'}₹{Math.abs(totals.signed).toLocaleString()}
        </Tag>
      </Space>

      <Card>
        {isLoading ? <Skeleton active /> :
          rows.length === 0 ? <Empty /> : (
            <Table rowKey="id" size="small" columns={cols as never}
                    dataSource={rows} pagination={{ pageSize: 50 }} />
          )}
      </Card>
    </div>
  )
}
